/**
 * Role types for RBAC access control
 */

export interface PolicyRule {
  resources: string[];
  actions: string[];
}

export interface RoleListItem {
  name: string;
  displayName?: string;
  isBuiltin: boolean;
  bindingCount: number;
  createdAt: string;
  createdByEmail?: string;
  updatedByEmail?: string;
}

export interface Role extends RoleListItem {
  description?: string;
  rules: PolicyRule[];
}

export interface RoleListResponse {
  items: RoleListItem[];
  count: number;
}

export interface CreateRoleRequest {
  name: string;
  displayName?: string;
  description?: string;
  rules: PolicyRule[];
}

export interface UpdateRoleRequest {
  displayName?: string;
  description?: string;
  rules?: PolicyRule[];
}

/** A single action that can be granted on a resource. */
export interface PermissionCatalogAction {
  name: string;
  displayName: string;
  description?: string;
}

/** A resource paired with the actions valid for it. */
export interface PermissionCatalogResource {
  name: string;
  displayName: string;
  description?: string;
  actions: PermissionCatalogAction[];
}

/** Response from GET /api/v1/authz/resources-actions — grouped catalog. */
export interface PermissionCatalogResponse {
  resources: PermissionCatalogResource[];
}

/** Human-readable labels for resources */
export const RESOURCE_LABELS: Record<string, string> = {
  catalogs: 'Catalogs',
  queryservices: 'Query Services',
  warmnodepools: 'Warm Node Pools',
  metadataservices: 'Metadata Services',
  schedules: 'Schedules',
  governances: 'Governance',
  accesstokens: 'Access Tokens',
  releasemanagers: 'Release Managers',
  storage: 'Storage',
  monitoringservices: 'Monitoring Services',
  namespaceconfigs: 'Namespace Configs',
  authgateways: 'Auth Gateways',
  groups: 'Groups',
  roles: 'Roles',
  rolebindings: 'Role Bindings',
  serviceaccounts: 'Service Accounts',
  compact: 'Compaction',
  agent: 'Agent',
  queryeditor: 'SQL Editor',
  '*': 'All Resources',
};

/** Human-readable labels for actions */
export const ACTION_LABELS: Record<string, string> = {
  create: 'Create',
  read: 'Read',
  update: 'Update',
  delete: 'Delete',
  list: 'List',
  refresh: 'Refresh',
  suspend: 'Suspend',
  resume: 'Resume',
  scale: 'Scale',
  revoke: 'Revoke',
  sync: 'Sync',
  compact: 'Compact',
  upgrade: 'Upgrade',
  execute: 'Execute',
  '*': 'All Actions',
};

/**
 * Build the standard "permission denied" message for a resource + action,
 * using the human-readable labels above. This is the single source of truth
 * for denial phrasing so disabled-action tooltips and inline error messages
 * stay uniform across the app.
 */
export function getPermissionDeniedReason(resource: string, action: string): string {
  // The generic "{action} {resource}" template reads badly for some pairs;
  // special-case those here so every call site keeps using this single
  // source of truth instead of inventing local strings.
  if (resource === 'queryeditor' && action === 'execute') {
    return "You don't have permission to run queries in the SQL Editor. Contact your administrator.";
  }
  const resourceLabel = RESOURCE_LABELS[resource] || resource;
  const actionLabel = ACTION_LABELS[action] || action;
  return `You don't have permission to ${actionLabel.toLowerCase()} ${resourceLabel.toLowerCase()}. Contact your administrator.`;
}
